import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  PersonOutline,
  PhoneIphone,
  CardGiftcard,
  WhatsApp,
  Instagram,
} from '@mui/icons-material';
import { api } from '../../../../services/api';
import bannerEvento from '../../../../assets/sorteo/banner-evento.png';
import bannerPremio from '../../../../assets/sorteo/banner-premio.png';

const WHATSAPP_NUMERO = '5493482222252'; // +54 9 3482 22-2252
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMERO}`;
const POLITICA_PRIVACIDAD_URL = 'https://passgo.com.ar/legal';
const COLOR_PRIMARIO = '#5656FF';
const COLOR_PRIMARIO_OSCURO = '#0B0060';

// heuristica simple para elegir un icono segun el texto de la pregunta
// (las preguntas son dinamicas por tarifa, no hay un campo "tipo" en el modelo)
const iconoPregunta = (texto = '') => {
  const t = texto.toLowerCase();
  if (t.includes('cel') || t.includes('telefono') || t.includes('whatsapp')) {
    return <PhoneIphone sx={{ color: COLOR_PRIMARIO, fontSize: '20px' }} />;
  }
  return <PersonOutline sx={{ color: COLOR_PRIMARIO, fontSize: '20px' }} />;
};

const SorteoLanding = ({ codigo }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [yaCargado, setYaCargado] = useState(false);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);

  // saca el margen reservado para el navbar fijo (esta pantalla no lo tiene)
  useEffect(() => {
    document.body.classList.add('sorteo-body');
    return () => document.body.classList.remove('sorteo-body');
  }, []);

  useEffect(() => {
    const fetchInfo = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.tickets.getInformacionAdicional(codigo);
        if (response.status === 'success') {
          setPreguntas(response.data.preguntas || []);
          setYaCargado(!!response.data.ya_cargado);
        } else {
          setError(response.message || 'No se pudo cargar el formulario');
        }
      } catch (e) {
        setError('Error de conexión');
      } finally {
        setLoading(false);
      }
    };
    if (codigo) fetchInfo();
  }, [codigo]);

  const handleChange = (preguntaId, valor) => {
    setRespuestas((prev) => ({ ...prev, [preguntaId]: valor }));
    setFormErrors((prev) => ({ ...prev, [preguntaId]: false }));
  };

  const validar = () => {
    const errores = {};
    let valido = true;

    preguntas.forEach((pregunta) => {
      if (pregunta.obligatoria === false) return;
      const respuesta = respuestas[pregunta.id];
      if (!respuesta || respuesta.trim() === '') {
        errores[pregunta.id] = true;
        valido = false;
      }
    });

    setFormErrors(errores);
    return valido && aceptaTerminos;
  };

  const handleSubmit = async () => {
    if (!validar()) return;

    setSaving(true);
    setError('');
    try {
      const response = await api.tickets.saveInformacionAdicional(codigo, respuestas);
      if (response.status === 'success') {
        setYaCargado(true);
      } else {
        setError(response.message || 'Error al guardar la información');
      }
    } catch (e) {
      setError('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#F3F4F6', minHeight: '100vh' }}>
      <div style={{ maxWidth: '480px', margin: '0 auto', backgroundColor: '#F3F4F6' }}>

        {/* banner del evento: mismo ancho que la tarjeta de abajo, la tarjeta se
            superpone por encima (margin-top negativo) */}
        <div style={{ padding: '0 16px' }}>
          <div style={{ backgroundColor: '#3A3A28', borderRadius: '16px', overflow: 'hidden', height: '160px' }}>
            <img src={bannerEvento} alt="Evento" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
          </div>
        </div>

        {/* tarjeta principal */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          margin: '-24px 16px 0',
          position: 'relative',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        }}>
          {/* banner del combo / premio, con espacio blanco alrededor */}
          <div style={{ padding: '16px 16px 0' }}>
            <img src={bannerPremio} alt="Premio" style={{ width: '100%', display: 'block', borderRadius: '10px' }} />
          </div>

          <div style={{ padding: '20px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <CircularProgress size={28} sx={{ color: COLOR_PRIMARIO }} />
              </div>
            ) : yaCargado ? (
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <p style={{
                  fontFamily: 'Bricolage Grotesque',
                  fontWeight: 600,
                  fontSize: '18px',
                  color: '#10B981',
                  marginBottom: '8px',
                }}>
                  ¡Ya estás participando! 🎉
                </p>
                <p style={{ fontFamily: 'Bricolage Grotesque', fontSize: '14px', color: '#6B7280', marginBottom: 0 }}>
                  Ya completaste tus datos para el sorteo. Mucha suerte.
                </p>
              </div>
            ) : error && preguntas.length === 0 ? (
              <p style={{ fontFamily: 'Bricolage Grotesque', color: '#f44336', textAlign: 'center' }}>
                {error}
              </p>
            ) : (
              <>
                {preguntas.map((pregunta) => (
                  <div key={pregunta.id} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      {iconoPregunta(pregunta.informacion_adicional)}
                      <span style={{ fontFamily: 'Bricolage Grotesque', fontSize: '14px', color: '#374151', fontWeight: 500 }}>
                        {pregunta.informacion_adicional}
                        {pregunta.obligatoria !== false && ' *'}
                      </span>
                    </div>
                    <TextField
                      fullWidth
                      size="small"
                      value={respuestas[pregunta.id] || ''}
                      onChange={(e) => handleChange(pregunta.id, e.target.value)}
                      error={!!formErrors[pregunta.id]}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          fontFamily: 'Bricolage Grotesque',
                        },
                      }}
                    />
                  </div>
                ))}

                <FormControlLabel
                  sx={{ alignItems: 'flex-start', mt: 1, ml: 0 }}
                  control={
                    <Checkbox
                      checked={aceptaTerminos}
                      onChange={(e) => setAceptaTerminos(e.target.checked)}
                      sx={{ padding: '2px', mr: 1, '&.Mui-checked': { color: COLOR_PRIMARIO } }}
                    />
                  }
                  label={
                    <span style={{ fontFamily: 'Bricolage Grotesque', fontSize: '13px', color: '#6B7280', lineHeight: '1.4' }}>
                      Acepto recibir información sobre el evento y novedades de nuestros aliados.{' '}
                      <a href={POLITICA_PRIVACIDAD_URL} target="_blank" rel="noreferrer" style={{ color: COLOR_PRIMARIO }}>
                        Política de privacidad.
                      </a>
                    </span>
                  }
                />

                {error && (
                  <p style={{ fontFamily: 'Bricolage Grotesque', color: '#f44336', fontSize: '13px', marginTop: '8px' }}>
                    {error}
                  </p>
                )}

                <Button
                  fullWidth
                  onClick={handleSubmit}
                  disabled={saving || !aceptaTerminos}
                  startIcon={saving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CardGiftcard />}
                  sx={{
                    mt: 2,
                    mx: '-4px',
                    width: 'calc(100% + 8px)',
                    backgroundColor: COLOR_PRIMARIO,
                    color: 'white',
                    fontFamily: 'Bricolage Grotesque',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '10px',
                    padding: '12px',
                    fontSize: '15px',
                    '&:hover': { backgroundColor: COLOR_PRIMARIO_OSCURO },
                    '&:disabled': { backgroundColor: '#cccccc', color: 'white' },
                  }}
                >
                  {saving ? 'Enviando...' : 'Participar del sorteo'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* cta whatsapp: el boton va a borde completo, sin espacio abajo, pisando
            el borde inferior de la tarjeta (mismo radius que la tarjeta) */}
        <div style={{
          backgroundColor: '#E6F7F1',
          borderRadius: '16px',
          margin: '20px 16px 0',
          paddingTop: '20px',
          overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '18px', padding: '0 20px' }}>
            <div style={{ maxWidth: '68%' }}>
              <p style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: '19px', color: '#111827', marginBottom: '8px' }}>
                Toda la Expo, en un solo chat
              </p>
              <p style={{ fontFamily: 'Bricolage Grotesque', fontSize: '13px', color: '#6B7280', marginBottom: 0 }}>
                Escribinos por WhatsApp y recibí información en tiempo real sobre horarios, actividades, novedades y todo lo que necesitás saber.
              </p>
            </div>
            <div style={{
              backgroundColor: '#25D366',
              borderRadius: '50%',
              width: '60px',
              height: '60px',
              minWidth: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              flexShrink: 0,
            }}>
              <WhatsApp sx={{ color: 'white', fontSize: '30px' }} />
            </div>
          </div>
          <Button
            fullWidth
            component="a"
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noreferrer"
            sx={{
              backgroundColor: COLOR_PRIMARIO,
              color: 'white',
              fontFamily: 'Bricolage Grotesque',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '10px 10px 16px 16px',
              padding: '14px',
              fontSize: '15px',
              '&:hover': { backgroundColor: COLOR_PRIMARIO_OSCURO },
            }}
          >
            Abrir chat
          </Button>
        </div>

        {/* footer: radius solo arriba, abajo pegado al borde de todo, como un footer real */}
        <div style={{
          backgroundColor: COLOR_PRIMARIO_OSCURO,
          borderRadius: '16px 16px 0 0',
          marginTop: '16px',
          padding: '20px',
          textAlign: 'center',
        }}>
          <p style={{ fontFamily: 'Bricolage Grotesque', color: 'white', fontSize: '14px', marginBottom: '8px' }}>
            Seguinos en nuestras redes
          </p>
          <a
            href="https://www.instagram.com/passgoeventos/"
            target="_blank"
            rel="noreferrer"
            style={{ display: 'flex', justifyContent: 'center', color: 'white', marginBottom: '8px' }}
          >
            <Instagram sx={{ fontSize: '18px' }} />
          </a>
          <p style={{ fontFamily: 'Bricolage Grotesque', color: '#B4B4FF', fontSize: '13px', marginBottom: 0 }}>
            www.passgo.com.ar
          </p>
        </div>
      </div>
    </div>
  );
};

export default SorteoLanding;
